import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LogoMain from '../assets/images/kamelesanpillay_20220620185113.png';
import { useCart } from '../shop/core/cart/CartContext';
import { useAuth } from '../auth/AuthContext';
import { apiGet } from '../services/api';
import { SHIPPING_CONFIG } from '../utils/constants';

const Header = () => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSinglesOpen, setIsSinglesOpen] = useState(false);
  const [singlesChildren, setSinglesChildren] = useState([]);
  const [singlesParentId, setSinglesParentId] = useState(null);
  const [isPokemonOpen, setIsPokemonOpen] = useState(false);
  const [pokemonCategories, setPokemonCategories] = useState([]);
  const [isOnePieceOpen, setIsOnePieceOpen] = useState(false);
  const [onePieceCategories, setOnePieceCategories] = useState([]);
  const [isAccessoriesOpen, setIsAccessoriesOpen] = useState(false);
  const [accessoriesChildren, setAccessoriesChildren] = useState([]);
  const navigate = useNavigate();
  
  // Get auth context
  const { isAuthenticated } = useAuth();

  // Free shipping threshold from environment
  const freeShippingThreshold = SHIPPING_CONFIG.FREE_SHIPPING_THRESHOLD;

  // Mobile detection effect
  useEffect(() => {
    const checkMobile = () => {
      const windowWidth = window.innerWidth;
      const isMobileDevice = windowWidth <= 767;
      const isTouchDevice = 'ontouchstart' in window;
      const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // More aggressive mobile detection
      const isDevToolsMobile = windowWidth <= 768; // Include common mobile breakpoints
      
      // Use multiple detection methods with more lenient logic
      const shouldBeMobile = isMobileDevice || isTouchDevice || isMobileUserAgent || isDevToolsMobile;
      
      // Force mobile if in DevTools mobile view
      if (windowWidth <= 768) {
        setIsMobile(true);
      } else {
        setIsMobile(shouldBeMobile);
      }
    };

    // Check on mount
    checkMobile();

    // Check on resize
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Mobile-friendly dropdown handlers
  const toggleDropdown = (dropdownType) => {
    // Close all other dropdowns
    if (dropdownType !== 'pokemon') setIsPokemonOpen(false);
    if (dropdownType !== 'onepiece') setIsOnePieceOpen(false);
    if (dropdownType !== 'singles') setIsSinglesOpen(false);
    if (dropdownType !== 'accessories') setIsAccessoriesOpen(false);
    
    // Toggle the selected dropdown
    switch (dropdownType) {
      case 'pokemon':
        setIsPokemonOpen(prev => !prev);
        break;
      case 'onepiece':
        setIsOnePieceOpen(prev => !prev);
        break;
      case 'singles':
        setIsSinglesOpen(prev => !prev);
        break;
      case 'accessories':
        setIsAccessoriesOpen(prev => !prev);
        break;
      default:
        break;
    }
  };


  // Fetch Singles parent and child categories on mount
  useEffect(() => {
    let isCancelled = false;
    (async () => {
      try {
        // First, get ALL parent categories to debug the Singles issue
        const allCategoriesResult = await apiGet('/products/categories', {
          parent: 0,
          per_page: 100,
        });
        
        
        // Find Singles by name (case-insensitive) since slug might be different
        const singlesCategory = allCategoriesResult?.data?.find(cat => 
          cat.name?.toLowerCase() === 'singles' || 
          cat.slug?.toLowerCase() === 'singles' ||
          cat.slug?.toLowerCase() === 'all-singles'
        );
        
        if (!isCancelled && singlesCategory) {
          setSinglesParentId(singlesCategory.id);
        } else if (process.env.NODE_ENV === 'development' && allCategoriesResult?.data?.length > 0) {
          // Only log in development mode if we have categories but Singles wasn't found
          console.debug('[Header] Singles parent category not found. Available categories:', allCategoriesResult.data.map(c => ({ id: c.id, name: c.name, slug: c.slug })));
        }
        
        // Then get the child categories
        const result = await apiGet('/products/categories/children', {
          parent_slug: 'singles',
          hide_empty: true,
          per_page: 100,
        });
        if (!isCancelled && result && result.success) {
          setSinglesChildren(Array.isArray(result.data) ? result.data : []);
        }
      } catch (e) {
        // Silently ignore for now; dropdown will just not appear
        if (!isCancelled) {
          setSinglesChildren([]);
          setSinglesParentId(null);
        }
      }
    })();
    return () => { isCancelled = true; };
  }, []);

  // Fetch Accessories child categories on mount
  useEffect(() => {
    let isCancelled = false;
    (async () => {
      try {
        const result = await apiGet('/products/categories/children', {
          parent_slug: 'accessories',
          hide_empty: true,
          per_page: 100,
        });
        if (!isCancelled && result && result.success) {
          setAccessoriesChildren(Array.isArray(result.data) ? result.data : []);
        }
      } catch (e) {
        if (!isCancelled) setAccessoriesChildren([]);
      }
    })();
    return () => { isCancelled = true; };
  }, []);

  // Fetch all categories once for brand-linked dropdowns (Pokemon / One Piece)
  useEffect(() => {
    let isCancelled = false;
    (async () => {
      try {
        const result = await apiGet('/products/categories', { hide_empty: false, per_page: 300, orderby: 'name', order: 'ASC' });
        const cats = (result && result.success ? result.data : result) || [];
        if (isCancelled) return;
        const byName = (needle) => cats.filter(c => String(c.name || '').toLowerCase().includes(needle));
        setPokemonCategories(byName('pokemon'));
        setOnePieceCategories(byName('one piece'));
      } catch (e) {
        if (!isCancelled) {
          setPokemonCategories([]);
          setOnePieceCategories([]);
        }
      }
    })();
    return () => { isCancelled = true; };
  }, []);

  // Scroll detection effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50); // Hide bar after scrolling 50px
    };

    window.addEventListener('scroll', handleScroll);
    
    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
  };

  const closeNav = () => {
    setIsNavOpen(false);
    setIsSinglesOpen(false);
    setIsAccessoriesOpen(false);
    setIsPokemonOpen(false);
    setIsOnePieceOpen(false);
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to shop page with search query
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsSearchOpen(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Close search on Escape key or outside click
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isSearchOpen) {
        setIsSearchOpen(false);
        setSearchQuery('');
      }
    };

    const handleClickOutside = (e) => {
      const target = e.target;
      if (isSearchOpen && !target.closest('[data-search-container]')) {
        setIsSearchOpen(false);
        setSearchQuery('');
      }
    };

    if (isSearchOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchOpen]);

  const { cart } = useCart();

  return (
    <header className="header">
      {/* overlay */}
      <div className={`overlay ${isNavOpen ? 'active' : ''}`} onClick={closeNav}></div>

      <div className="container">
        <Link to="/" className="logo" onClick={scrollToTop}>
          <img src={LogoMain} alt="GameX logo" />
        </Link>

        <button className="nav-open-btn" onClick={toggleNav}>
          <ion-icon name="menu-outline"></ion-icon>
        </button>

        <nav className={`navbar ${isNavOpen ? 'active' : ''}`}>
          <div className="navbar-top">
            <Link to="/" className="logo" onClick={() => { closeNav(); scrollToTop(); }}>
              <img src={LogoMain} alt="GameX logo" />
            </Link>

            <button className="nav-close-btn" onClick={closeNav}>
              <ion-icon name="close-outline"></ion-icon>
            </button>
          </div>

          <ul className="navbar-list" style={{justifyContent: 'center', width: '100%'}}>
            <li>
              <Link to="/shop" className="navbar-link" onClick={closeNav}>Shop</Link>
            </li>
            <li className="navbar-item pokemon-dropdown" style={{ borderBottom: isMobile ? '1px solid hsla(0, 0%, 100%, 0.1)' : 'none', width: isMobile ? '100%' : 'auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'space-between' : 'center', width: isMobile ? '100%' : 'auto' }}>
                <Link to="/shop?brand=Pokemon" className="navbar-link" onClick={closeNav} style={{ border: 'none', flex: isMobile ? 1 : 'unset' }}>Pokemon</Link>
                {!isMobile && (
                  <span className="navbar-chevron" aria-hidden="true">▾</span>
                )}
                {isMobile && (
                <button 
                  type="button"
                  aria-label="Toggle Pokemon submenu"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDropdown('pokemon'); }}
                  aria-haspopup="true"
                  aria-expanded={isPokemonOpen}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', padding: '10px 25px' }}
                >
                  <span style={{ fontSize: 18, color: 'var(--orange)', transform: isPokemonOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}>▾</span>
                </button>
                )}
              </div>
              {isPokemonOpen && pokemonCategories && pokemonCategories.length > 0 && (
                <ul 
                  className="mobile-dropdown-menu"
                  style={{
                    position: 'relative',
                    width: '100%',
                    background: 'rgba(255,255,255,0.05)',
                    border: 'none',
                    borderRadius: '0',
                    padding: '0',
                    margin: '0',
                    boxShadow: 'none',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}
                >
                  {pokemonCategories.map((cat) => (
                    <li key={`pk-${cat.id}`} style={{ listStyle: 'none' }}>
                      <Link 
                        to={`/shop?category=${encodeURIComponent(cat.name)}`}
                        className="navbar-link"
                        onClick={closeNav}
                        style={{ 
                          display: 'block', 
                          padding: '12px 25px', 
                          whiteSpace: 'nowrap',
                          borderBottom: '1px solid hsla(0, 0%, 100%, 0.1)',
                          fontSize: '14px',
                          width: '100%'
                        }}
                      >
                        {cat.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              {!isMobile && pokemonCategories && pokemonCategories.length > 0 && (
                <ul className="desktop-dropdown-menu">
                  {pokemonCategories.map((cat) => (
                    <li key={`pkd-${cat.id}`}>
                      <Link 
                        to={`/shop?category=${encodeURIComponent(cat.name)}`}
                        className="dropdown-link"
                        onClick={closeNav}
                      >
                        {cat.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
            <li className="navbar-item onepiece-dropdown" style={{ borderBottom: isMobile ? '1px solid hsla(0, 0%, 100%, 0.1)' : 'none', width: isMobile ? '100%' : 'auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'space-between' : 'center', width: isMobile ? '100%' : 'auto' }}>
                <Link to="/shop?brand=One%20Piece" className="navbar-link" onClick={closeNav} style={{ border: 'none', flex: isMobile ? 1 : 'unset' }}>One Piece</Link>
                {!isMobile && (
                  <span className="navbar-chevron" aria-hidden="true">▾</span>
                )}
                {isMobile && (
                <button 
                  type="button"
                  aria-label="Toggle One Piece submenu"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDropdown('onepiece'); }}
                  aria-haspopup="true"
                  aria-expanded={isOnePieceOpen}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', padding: '10px 25px' }}
                >
                  <span style={{ fontSize: 18, color: 'var(--orange)', transform: isOnePieceOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}>▾</span>
                </button>
                )}
              </div>
              {isOnePieceOpen && onePieceCategories && onePieceCategories.length > 0 && (
                <ul 
                  className="mobile-dropdown-menu"
                  style={{
                    position: 'relative',
                    width: '100%',
                    background: 'rgba(255,255,255,0.05)',
                    border: 'none',
                    borderRadius: '0',
                    padding: '0',
                    margin: '0',
                    boxShadow: 'none',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}
                >
                  {onePieceCategories.map((cat) => (
                    <li key={`op-${cat.id}`} style={{ listStyle: 'none' }}>
                      <Link 
                        to={`/shop?category=${encodeURIComponent(cat.name)}`}
                        className="navbar-link"
                        onClick={closeNav}
                        style={{ 
                          display: 'block', 
                          padding: '12px 25px', 
                          whiteSpace: 'nowrap',
                          borderBottom: '1px solid hsla(0, 0%, 100%, 0.1)',
                          fontSize: '14px',
                          width: '100%'
                        }}
                      >
                        {cat.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              {!isMobile && onePieceCategories && onePieceCategories.length > 0 && (
                <ul className="desktop-dropdown-menu">
                  {onePieceCategories.map((cat) => (
                    <li key={`opd-${cat.id}`}>
                      <Link 
                        to={`/shop?category=${encodeURIComponent(cat.name)}`}
                        className="dropdown-link"
                        onClick={closeNav}
                      >
                        {cat.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
            <li className="navbar-item singles-dropdown" style={{ borderBottom: isMobile ? '1px solid hsla(0, 0%, 100%, 0.1)' : 'none', width: isMobile ? '100%' : 'auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'space-between' : 'center', width: isMobile ? '100%' : 'auto' }}>
                <Link 
                  to={singlesParentId ? `/shop?category=${singlesParentId}` : "/shop?category=Singles"} 
                  className="navbar-link" 
                  onClick={closeNav}
                  style={{ border: 'none', flex: isMobile ? 1 : 'unset' }}
                >
                  Singles
                </Link>
                {!isMobile && (
                  <span className="navbar-chevron" aria-hidden="true">▾</span>
                )}
                {isMobile && (
                <button 
                  type="button"
                  aria-label="Toggle Singles submenu"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDropdown('singles'); }}
                  aria-haspopup="true"
                  aria-expanded={isSinglesOpen}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', padding: '10px 25px' }}
                >
                  <span style={{ fontSize: 18, color: 'var(--orange)', transform: isSinglesOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}>▾</span>
                </button>
                )}
              </div>
              {isSinglesOpen && singlesChildren && singlesChildren.length > 0 && (
                <ul 
                  className="mobile-dropdown-menu"
                  style={{
                    position: 'relative',
                    width: '100%',
                    background: 'rgba(255,255,255,0.05)',
                    border: 'none',
                    borderRadius: '0',
                    padding: '0',
                    margin: '0',
                    boxShadow: 'none',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}
                >
                  {singlesChildren.map((cat) => (
                    <li key={cat.id} style={{ listStyle: 'none' }}>
                      <Link 
                        to={`/shop?category=${cat.id}`}
                        className="navbar-link"
                        onClick={closeNav}
                        style={{
                          display: 'block',
                          padding: '12px 25px',
                          whiteSpace: 'nowrap',
                          borderBottom: '1px solid hsla(0, 0%, 100%, 0.1)',
                          fontSize: '14px'
                        }}
                      >
                        {cat.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              {!isMobile && singlesChildren && singlesChildren.length > 0 && (
                <ul className="desktop-dropdown-menu">
                  {singlesChildren.map((cat) => (
                    <li key={`snd-${cat.id}`}>
                      <Link 
                        to={`/shop?category=${cat.id}`}
                        className="dropdown-link"
                        onClick={closeNav}
                      >
                        {cat.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
            <li className="navbar-item accessories-dropdown" style={{ borderBottom: isMobile ? '1px solid hsla(0, 0%, 100%, 0.1)' : 'none', width: isMobile ? '100%' : 'auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'space-between' : 'center', width: isMobile ? '100%' : 'auto' }}>
                <Link 
                  to="/shop?category=Accessories" 
                  className="navbar-link" 
                  onClick={closeNav}
                  style={{ border: 'none', flex: isMobile ? 1 : 'unset' }}
                >
                  Accessories
                </Link>
                {!isMobile && (
                  <span className="navbar-chevron" aria-hidden="true">▾</span>
                )}
                {isMobile && (
                <button 
                  type="button"
                  aria-label="Toggle Accessories submenu"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDropdown('accessories'); }}
                  aria-haspopup="true"
                  aria-expanded={isAccessoriesOpen}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', padding: '10px 25px' }}
                >
                  <span style={{ fontSize: 18, color: 'var(--orange)', transform: isAccessoriesOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}>▾</span>
                </button>
                )}
              </div>
              {isAccessoriesOpen && accessoriesChildren && accessoriesChildren.length > 0 && (
                <ul 
                  className="mobile-dropdown-menu"
                  style={{
                    position: 'relative',
                    width: '100%',
                    background: 'rgba(255,255,255,0.05)',
                    border: 'none',
                    borderRadius: '0',
                    padding: '0',
                    margin: '0',
                    boxShadow: 'none',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}
                >
                  {accessoriesChildren.map((cat) => (
                    <li key={cat.id} style={{ listStyle: 'none' }}>
                      <Link 
                        to={`/shop?category=${encodeURIComponent(cat.name)}`}
                        className="navbar-link"
                        onClick={closeNav}
                        style={{
                          display: 'block',
                          padding: '12px 25px',
                          whiteSpace: 'nowrap',
                          borderBottom: '1px solid hsla(0, 0%, 100%, 0.1)',
                          fontSize: '14px'
                        }}
                      >
                        {cat.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              {!isMobile && accessoriesChildren && accessoriesChildren.length > 0 && (
                <ul className="desktop-dropdown-menu">
                  {accessoriesChildren.map((cat) => (
                    <li key={`acd-${cat.id}`}>
                      <Link 
                        to={`/shop?category=${encodeURIComponent(cat.name)}`}
                        className="dropdown-link"
                        onClick={closeNav}
                      >
                        {cat.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
            <li>
              <Link to="/shop?onSale=true" className="navbar-link" onClick={closeNav}>Sale</Link>
            </li>
            {/* Hide Tournaments and Contact when search is open */}
            {!isSearchOpen && (
              <>
                <li>
                  <Link to="/contact" className="navbar-link" onClick={closeNav}>Contact</Link>
                </li>
              </>
            )}
          </ul>

          {/* Mobile-only menu items (Cart, Login, Social) */}
          {isMobile && (
            <ul className="nav-social-list" style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: '10px',
              padding: '0 25px',
              marginTop: '20px'
            }}>
              <li style={{ width: '100%', listStyle: 'none' }}>
                <Link to="/cart" className="btn-sign_in" title="Cart" onClick={closeNav} style={{
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  gap: '10px', 
                  padding: '12px 20px', 
                  margin: '0',
                  background: 'var(--orange)',
                  color: 'var(--raisin-black-1)',
                  borderRadius: '8px',
                  border: '2px solid var(--orange)',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  width: '100%',
                  boxSizing: 'border-box'
                }}>
                  <span className="icon-box">
                    <ion-icon name="cart-outline"></ion-icon>
                  </span>
                  <span>Cart ({cart.itemCount || 0})</span>
                </Link>
              </li>
              <li style={{ width: '100%', listStyle: 'none' }}>
                {!isAuthenticated ? (
                  <Link to="/login" className="btn-sign_in" title="Login" onClick={closeNav} style={{
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '10px', 
                    padding: '12px 20px', 
                    margin: '0',
                    background: 'transparent',
                    color: 'white',
                    borderRadius: '8px',
                    border: '2px solid var(--orange)',
                    transition: 'all 0.3s ease',
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}>
                    <span className="icon-box">
                      <ion-icon name="log-in-outline"></ion-icon>
                    </span>
                    <span>Login</span>
                  </Link>
                ) : (
                  <Link to="/user/dashboard" className="btn-sign_in" title="Account" onClick={closeNav} style={{
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '10px', 
                    padding: '12px 20px', 
                    margin: '0',
                    background: 'transparent',
                    color: 'white',
                    borderRadius: '8px',
                    border: '2px solid var(--orange)',
                    transition: 'all 0.3s ease',
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}>
                    <span className="icon-box">
                      <ion-icon name="person-outline"></ion-icon>
                    </span>
                    <span>Account</span>
                  </Link>
                )}
              </li>
              <li style={{ 
                width: '100%', 
                listStyle: 'none',
                display: 'flex',
                justifyContent: 'center',
                gap: '15px',
                padding: '15px 0',
                borderTop: '1px solid hsla(0, 0%, 100%, 0.1)',
                marginTop: '10px'
              }}>
                <a href="https://wa.me/27825655408" target="_blank" rel="noopener noreferrer" className="social-link" title="WhatsApp" style={{ color: 'var(--orange)', fontSize: '24px' }}>
                  <ion-icon name="logo-whatsapp"></ion-icon>
                </a>
                <a href="https://www.instagram.com/oraclegamingza?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" target="_blank" rel="noopener noreferrer" className="social-link" title="Instagram" style={{ color: 'var(--orange)', fontSize: '24px' }}>
                  <ion-icon name="logo-instagram"></ion-icon>
                </a>
                <a href="https://www.facebook.com/p/Oracle-Gaming-100085081547257/" target="_blank" rel="noopener noreferrer" className="social-link" title="Facebook" style={{ color: 'var(--orange)', fontSize: '24px' }}>
                  <ion-icon name="logo-facebook"></ion-icon>
                </a>
              </li>
            </ul>
          )}
        </nav>

        <div className="header-actions">
          <div className="search-container" data-search-container>
            <button className="search" onClick={toggleSearch}>
              <ion-icon name="search-outline"></ion-icon>
            </button>
            
            {/* Search Input with horizontal expansion */}
            <div className={`search-expand ${isSearchOpen ? 'search-open' : ''}`}>
              <form className="search-form" onSubmit={handleSearch}>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                  autoFocus={isSearchOpen}
                />
                <button 
                  type="button" 
                  className="search-close"
                  onClick={() => {
                    setIsSearchOpen(false);
                    setSearchQuery('');
                  }}
                >
                  <ion-icon name="close-outline"></ion-icon>
                </button>
              </form>
            </div>
          </div>
          
          {/* Auth Links or User Menu */}
          {!isAuthenticated ? (
            <Link to="/login" className="btn-sign_in" title="Login" style={{ marginLeft: '10px', marginRight: '10px' }}>
              <span className="icon-box">
                <ion-icon name="log-in-outline"></ion-icon>
              </span>
            </Link>
          ) : (
            <Link to="/user/dashboard" className="btn-sign_in" title="Account" style={{ marginLeft: '10px', marginRight: '10px' }}>
              <span className="icon-box">
                <ion-icon name="person-outline"></ion-icon>
              </span>
            </Link>
          )}

          
          <Link to="/cart" className="btn-sign_in" title="Cart" style={{ marginLeft: '10px' }}>
            <span className="icon-box">
              <ion-icon name="cart-outline"></ion-icon>
            </span>
            <span>Cart ({cart.itemCount || 0})</span>
          </Link>
        </div>
      </div>

       {/* Mobile Shipping Banner - Only show on mobile with blue styling */}
       {isMobile && (
         <div className={`shipping-banner-mobile-container ${isScrolled ? 'shipping-banner-hidden' : 'shipping-banner-visible'}`}
              style={{ 
                background: 'transparent', 
                borderTop: 'none',
                transition: 'all 0.5s ease-out',
                overflow: 'hidden',
                position: 'relative',
                width: '100%',
                maxHeight: isScrolled ? '0' : '54px',
                opacity: isScrolled ? 0 : 1,
                margin: '0',
                padding: '0',
                borderBottom: 'none',
                boxShadow: 'none'
              }}>
          {/* Blue bar between header and shipping banner */}
          <div style={{
            width: '100%',
            height: '4px',
            background: '#14fefb',
            position: 'relative',
            zIndex: 1
          }}></div>
          <div className="shipping-banner-content" style={{ position: 'relative', width: '100%' }}>
            <div className="container">
              <div className="shipping-banner-inner" style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                padding: '6px 0', 
                gap: '6px', 
                textAlign: 'center',
                height: '50px',
                minHeight: '50px',
                width: '100%'
              }}>
                <div className="shipping-banner-main" style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px', 
                  justifyContent: 'center',
                  width: '100%',
                  textAlign: 'center'
                }}>
                   <div className="shipping-banner-icon" style={{ 
                     background: '#14fefb', 
                     color: '#1a1a2e', 
                     padding: '6px', 
                     borderRadius: '50%', 
                     display: 'flex', 
                     alignItems: 'center', 
                     justifyContent: 'center', 
                     fontSize: '14px', 
                     fontWeight: 'bold' 
                   }}>
                    <ion-icon name="cube-outline"></ion-icon>
                  </div>
                  <div className="shipping-banner-text">
                    <span className="shipping-banner-title" style={{ 
                      color: 'white', 
                      fontFamily: 'Oswald, sans-serif', 
                      fontSize: '14px', 
                      fontWeight: '700', 
                      letterSpacing: '0.5px' 
                    }}>Free Shipping on orders over R{freeShippingThreshold}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Shipping Banner - Only show on desktop with CSS classes */}
      {!isMobile && (
        <div className={`shipping-banner-desktop ${isScrolled ? 'shipping-banner-hidden' : 'shipping-banner-visible'}`}>
          <div className="shipping-banner-content">
            <div className="container">
              <div className="shipping-banner-inner">
                <div className="shipping-banner-main">
                  <div className="shipping-banner-icon">
                    <ion-icon name="cube-outline"></ion-icon>
                  </div>
                  <div className="shipping-banner-text">
                    <span className="shipping-banner-title">Free Shipping on orders over R{freeShippingThreshold}</span>
                  </div>
                </div>
                
                {/* Desktop extra content */}
                <div className="shipping-banner-extra-desktop">
                  <div className="shipping-banner-item">
                    <ion-icon name="gift-outline"></ion-icon>
                    <span>Track your order</span>
                  </div>
                  <div className="shipping-banner-item">
                    <ion-icon name="time-outline"></ion-icon>
                    <span>3-5 day delivery</span>
                  </div>
                  <Link 
                    to="/shipping-policy" 
                    className="shipping-banner-link"
                  >
                    View shipping policy
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
